// ═══════════════════════════════════════════════════════════════════
// catalogoFull.js — Helpers frontend del catálogo de Full MLA
// ═══════════════════════════════════════════════════════════════════
// Consumidores:
//   - Admin UI (TablaFullMLA.jsx): getFullVigente, listarHistorialFull
//   - Motor de rentabilidad (pre-venta): clasificarTamanio, calcularCostoFull
//
// El motor necesita responder la pregunta "¿cuánto cuesta tener este
// producto en Full durante N días?" — se alimenta con las dimensiones
// que devuelve `GET /items/{id}.shipping.dimensions` (cm y gramos).
// ═══════════════════════════════════════════════════════════════════

import {
  collection, getDocs, query, where, orderBy, limit as firestoreLimit,
} from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'

const COL = 'catalogoFullMLA'

// ── Lectura ─────────────────────────────────────────────────────────

export async function getFullVigente() {
  const snap = await getDocs(
    query(
      collection(db, COL),
      where('vigenteHasta', '==', null),
      firestoreLimit(1),
    )
  )
  if (snap.empty) return null
  const doc = snap.docs[0]
  return docConId(doc)
}

export async function listarHistorialFull() {
  const snap = await getDocs(
    query(
      collection(db, COL),
      orderBy('vigenteDesde', 'desc'),
    )
  )
  return snap.docs.map(docConId)
}

// ── Clasificador de tamaño ──────────────────────────────────────────

/**
 * Calcula el peso facturable de un ítem según la regla de ML:
 * máximo entre peso físico y peso volumétrico.
 *
 * @param {object} dimensions     { height, width, length, weight }
 *                                — cm y GRAMOS (formato ML)
 * @param {number} divisor        Divisor volumétrico (default 4000)
 * @returns {object} { pesoFisicoKg, pesoVolumetricoKg, pesoFacturableKg }
 */
export function calcularPesoFacturable(dimensions, divisor = 4000) {
  if (!dimensions) return null
  const { height, width, length, weight } = dimensions
  const pesoFisicoKg = Number(weight || 0) / 1000
  const pesoVolumetricoKg =
    Number(height || 0) * Number(width || 0) * Number(length || 0) / divisor
  const pesoFacturableKg = Math.max(pesoFisicoKg, pesoVolumetricoKg)
  return { pesoFisicoKg, pesoVolumetricoKg, pesoFacturableKg }
}

/**
 * Clasifica el tamaño de un ítem (pequeño / mediano / grande / extragrande)
 * según sus dimensiones y peso facturable.
 *
 * Regla: el ítem cabe en el bracket más chico donde sus 3 dimensiones
 * (sorted ascending) sean todas ≤ que las del bracket Y el peso facturable
 * sea ≤ pesoMaxKg. Extragrande es catch-all (sin dimMax/pesoMax).
 *
 * @param {object} dimensions  { height, width, length, weight } (cm y g)
 * @param {object} catalogo    Doc vigente de catalogoFullMLA
 * @returns {object|null} Tamaño matcheado o null si faltan dimensiones
 */
export function clasificarTamanio(dimensions, catalogo) {
  if (!dimensions || !catalogo?.tamanios?.length) return null

  const divisor = catalogo.volumetrico?.divisor || 4000
  const { pesoFacturableKg } = calcularPesoFacturable(dimensions, divisor) || {}

  const dimsProducto = [
    Number(dimensions.height || 0),
    Number(dimensions.width || 0),
    Number(dimensions.length || 0),
  ].sort((a, b) => a - b)

  for (const t of catalogo.tamanios) {
    // Extragrande / catch-all: sin dimMax y sin pesoMaxKg
    if (t.alto == null && t.ancho == null && t.largo == null) {
      return t
    }
    const dimsBracket = [
      Number(t.alto || 0),
      Number(t.ancho || 0),
      Number(t.largo || 0),
    ].sort((a, b) => a - b)
    const cabeDims = dimsProducto.every((v, i) => v <= dimsBracket[i])
    const pesoOk = t.pesoMaxKg == null || pesoFacturableKg <= t.pesoMaxKg
    if (cabeDims && pesoOk) return t
  }

  // Fallback: el último (suele ser extragrande)
  return catalogo.tamanios[catalogo.tamanios.length - 1]
}

// ── Cálculo de costo ────────────────────────────────────────────────

/**
 * Costo de tener un ítem en Full durante N días.
 *
 * @param {object} dimensions  { height, width, length, weight }
 * @param {number} dias        Cantidad de días
 * @param {object} catalogo    Doc vigente
 * @returns {object} { tamanio, pesoFacturableKg, tarifaDiaria, dias, costo }
 */
export function calcularCostoFull(dimensions, dias, catalogo) {
  if (!catalogo) return { error: 'catálogo no existe' }
  if (!dimensions) return { error: 'item sin dimensiones' }

  const divisor = catalogo.volumetrico?.divisor || 4000
  const pesos = calcularPesoFacturable(dimensions, divisor)
  const tamanio = clasificarTamanio(dimensions, catalogo)
  if (!tamanio) return { error: 'no se pudo clasificar el tamaño' }

  const tarifaDiaria = Number(tamanio.tarifaDiaria) || 0
  const diasN = Number(dias) || 0
  const costo = +(tarifaDiaria * diasN).toFixed(2)

  return {
    tamanio,
    pesoFisicoKg:       pesos?.pesoFisicoKg ?? null,
    pesoVolumetricoKg:  pesos?.pesoVolumetricoKg ?? null,
    pesoFacturableKg:   pesos?.pesoFacturableKg ?? null,
    tarifaDiaria,
    dias: diasN,
    costo,
  }
}

/**
 * Calcula el costo para TODAS las opciones de días configuradas en el catálogo.
 * Útil para renderizar en el simulador los 4-6 escenarios típicos
 * (1, 7, 15, 30, 45, 60 días).
 *
 * @param {object} dimensions
 * @param {object} catalogo
 * @returns {object} { tamanio, pesoFacturableKg, tarifaDiaria, escenarios: [{dias, costo}] }
 */
export function calcularCostoFullTodosLosDias(dimensions, catalogo) {
  if (!catalogo) return { error: 'catálogo no existe' }
  if (!dimensions) return { error: 'item sin dimensiones' }

  const divisor = catalogo.volumetrico?.divisor || 4000
  const pesos = calcularPesoFacturable(dimensions, divisor)
  const tamanio = clasificarTamanio(dimensions, catalogo)
  if (!tamanio) return { error: 'no se pudo clasificar el tamaño' }

  const tarifaDiaria = Number(tamanio.tarifaDiaria) || 0
  const opciones = Array.isArray(catalogo.opcionesDias) && catalogo.opcionesDias.length > 0
    ? catalogo.opcionesDias
    : [1, 7, 15, 30, 45, 60]

  const escenarios = opciones.map(d => ({
    dias: d,
    costo: +(tarifaDiaria * d).toFixed(2),
  }))

  return {
    tamanio,
    pesoFisicoKg:       pesos?.pesoFisicoKg ?? null,
    pesoVolumetricoKg:  pesos?.pesoVolumetricoKg ?? null,
    pesoFacturableKg:   pesos?.pesoFacturableKg ?? null,
    tarifaDiaria,
    escenarios,
  }
}
